import {
  useMemo,
  useState,
  useContext,
  useCallback,
  createContext,
  type ReactNode,
} from 'react';

// ----------------------------------------------------------------------
// Session-scoped dismissal tracking for in-app notifications (e.g.
// InterviewLimitBanner's "approaching limit" variant, plan-warning toasts in
// usePlanAccess). State is held in React state — explicitly NOT module-level
// — so it resets on full reload and is testable. Phase 2 review flagged
// module-level mutable Sets as a P1 issue; this context is the replacement.
// ----------------------------------------------------------------------

type NotificationDismissalContextValue = {
  isDismissed: (key: string) => boolean;
  dismiss: (key: string) => void;
  /**
   * Drops every recorded key. Callers scoping by org (e.g. usePlanAccess)
   * use this when the active org changes so the new org gets a fresh slate.
   */
  reset: () => void;
};

const NotificationDismissalContext =
  createContext<NotificationDismissalContextValue | null>(null);

// ----------------------------------------------------------------------

type NotificationDismissalProviderProps = {
  children: ReactNode;
};

export function NotificationDismissalProvider({
  children,
}: NotificationDismissalProviderProps) {
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());

  const isDismissed = useCallback((key: string) => dismissed.has(key), [dismissed]);

  const dismiss = useCallback((key: string) => {
    setDismissed((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setDismissed((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);

  const value = useMemo<NotificationDismissalContextValue>(
    () => ({ isDismissed, dismiss, reset }),
    [isDismissed, dismiss, reset]
  );

  return (
    <NotificationDismissalContext.Provider value={value}>
      {children}
    </NotificationDismissalContext.Provider>
  );
}

export function useNotificationDismissal(): NotificationDismissalContextValue {
  const ctx = useContext(NotificationDismissalContext);
  if (!ctx) {
    throw new Error(
      'useNotificationDismissal must be used within a NotificationDismissalProvider'
    );
  }
  return ctx;
}
